import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';

interface ImageCropModalProps {
  imageFile: File;
  open: boolean;
  onApply: (horizontalOffset: number | null, verticalOffset: number | null) => void;
  onCancel: () => void;
}

export default function ImageCropModal({
  imageFile,
  open,
  onApply,
  onCancel,
}: ImageCropModalProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_imageUrl, setImageUrl] = useState<string>('');
  const [upscaledImageUrl, setUpscaledImageUrl] = useState<string>('');
  const [horizontalOffset, setHorizontalOffset] = useState<number>(0.5); // 0 = left, 1 = right
  const [verticalOffset, setVerticalOffset] = useState<number>(0.5); // 0 = top, 1 = bottom
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [upscaledDimensions, setUpscaledDimensions] = useState<{ width: number; height: number } | null>(null);
  const [isPortrait, setIsPortrait] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartOffset, setDragStartOffset] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const TARGET_HEIGHT = 1080;
  const TARGET_WIDTH = 1080;
  const TARGET_SIZE = 1080; // 1080x1080 square

  // Track container width and height for positioning calculations
  useEffect(() => {
    if (!open) return;
    
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
        setContainerHeight(containerRef.current.offsetHeight);
      }
    };
    
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      updateDimensions();
    });
    
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [open]);

  // Create object URL for the original image
  useEffect(() => {
    if (!imageFile) return;
    
    // Reset offsets when image changes
    setHorizontalOffset(0.5);
    setVerticalOffset(0.5);
    setIsDragging(false);
    
    const url = URL.createObjectURL(imageFile);
    setImageUrl(url);
    let currentUpscaledUrl: string | null = null;
    
    // Load image to get dimensions and create upscaled version
    const img = new Image();
    img.onload = () => {
      setImageDimensions({ width: img.width, height: img.height });
      
      // Determine if image is portrait (width < height)
      const portrait = img.width < img.height;
      setIsPortrait(portrait);
      
      let upscaledWidth: number;
      let upscaledHeight: number;
      
      if (portrait) {
        // Portrait: scale to 1080px width
        const scale = TARGET_WIDTH / img.width;
        upscaledWidth = TARGET_WIDTH;
        upscaledHeight = Math.round(img.height * scale);
      } else {
        // Landscape: scale to 1080px height
        const scale = TARGET_HEIGHT / img.height;
        upscaledWidth = Math.round(img.width * scale);
        upscaledHeight = TARGET_HEIGHT;
      }
      
      setUpscaledDimensions({ width: upscaledWidth, height: upscaledHeight });
      
      // Create upscaled image on canvas
      const canvas = document.createElement('canvas');
      canvas.width = upscaledWidth;
      canvas.height = upscaledHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, upscaledWidth, upscaledHeight);
        canvas.toBlob((blob) => {
          if (blob) {
            const upscaledUrl = URL.createObjectURL(blob);
            currentUpscaledUrl = upscaledUrl;
            setUpscaledImageUrl(upscaledUrl);
          }
        }, 'image/png');
      }
    };
    img.onerror = (error) => {
      console.error('Failed to load image:', error);
    };
    img.src = url;
    
    return () => {
      URL.revokeObjectURL(url);
      if (currentUpscaledUrl) {
        URL.revokeObjectURL(currentUpscaledUrl);
      }
    };
  }, [imageFile]);

  // Handle mouse drag for crop position
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!upscaledDimensions || !containerRef.current) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartY(e.clientY);
    setDragStartOffset(isPortrait ? verticalOffset : horizontalOffset);
  };

  // Handle touch drag for crop position (mobile support)
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!upscaledDimensions || !containerRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStartX(touch.clientX);
    setDragStartY(touch.clientY);
    setDragStartOffset(isPortrait ? verticalOffset : horizontalOffset);
  };

  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!isDragging || !containerRef.current || !upscaledDimensions) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    
    // Calculate the displayed image dimensions (matching objectFit: contain behavior)
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    const imageAspectRatio = upscaledDimensions.width / upscaledDimensions.height;
    const { displayedWidth, displayedHeight } = calculateDisplayedImageDimensions(
      containerWidth,
      containerHeight,
      imageAspectRatio
    );
    
    if (isPortrait) {
      // Portrait: vertical cropping
      const deltaY = clientY - dragStartY;
      const maxCrop = upscaledDimensions.height - TARGET_SIZE;
      if (maxCrop <= 0) return; // Image is already square or shorter
      
      // Calculate the scale factor between displayed pixels and actual image pixels
      const pixelScale = upscaledDimensions.height / displayedHeight;
      
      // Convert movement (in screen pixels) to image pixels, then to offset
      const deltaInImagePixels = deltaY * pixelScale;
      const deltaOffset = deltaInImagePixels / maxCrop;
      
      const newOffset = Math.max(0, Math.min(1, dragStartOffset + deltaOffset));
      setVerticalOffset(newOffset);
    } else {
      // Landscape: horizontal cropping
      const deltaX = clientX - dragStartX;
      const maxCrop = upscaledDimensions.width - TARGET_SIZE;
      if (maxCrop <= 0) return; // Image is already square or smaller
      
      // Calculate the scale factor between displayed pixels and actual image pixels
      const pixelScale = upscaledDimensions.width / displayedWidth;
      
      // Convert movement (in screen pixels) to image pixels, then to offset
      const deltaInImagePixels = deltaX * pixelScale;
      const deltaOffset = deltaInImagePixels / maxCrop;
      
      const newOffset = Math.max(0, Math.min(1, dragStartOffset + deltaOffset));
      setHorizontalOffset(newOffset);
    }
  }, [isDragging, dragStartX, dragStartY, dragStartOffset, upscaledDimensions, isPortrait]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      handleMove(touch.clientX, touch.clientY);
    }
  }, [handleMove]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  const handleApply = () => {
    if (isPortrait) {
      onApply(null, verticalOffset);
    } else {
      onApply(horizontalOffset, null);
    }
  };

  // Helper function to calculate actual displayed image dimensions
  // This matches the behavior of objectFit: contain - image fits within container
  const calculateDisplayedImageDimensions = (
    containerWidth: number,
    containerHeight: number,
    imageAspectRatio: number
  ): { displayedWidth: number; displayedHeight: number } => {
    // Calculate what the dimensions would be if filling height
    const heightFilledWidth = containerHeight * imageAspectRatio;
    // Calculate what the dimensions would be if filling width
    const widthFilledHeight = containerWidth / imageAspectRatio;
    
    // Use the constraint that fits within both (objectFit: contain behavior)
    let displayedWidth: number;
    let displayedHeight: number;
    
    if (heightFilledWidth <= containerWidth) {
      // Image fits width when filling height - use height constraint
      displayedHeight = containerHeight;
      displayedWidth = heightFilledWidth;
    } else {
      // Image fits height when filling width - use width constraint
      displayedWidth = containerWidth;
      displayedHeight = widthFilledHeight;
    }
    
    return { displayedWidth, displayedHeight };
  };

  // Helper function to calculate image absolute position (matching CSS centering)
  const calculateImagePosition = (
    containerWidth: number,
    containerHeight: number,
    displayedWidth: number,
    displayedHeight: number
  ) => {
    // Match the CSS: left: 50%, top: 50%, transform: translate(-50%, -50%)
    const imageLeftAbsolute = (containerWidth - displayedWidth) / 2;
    const imageRightAbsolute = imageLeftAbsolute + displayedWidth;
    const imageTopAbsolute = (containerHeight - displayedHeight) / 2;
    const imageBottomAbsolute = imageTopAbsolute + displayedHeight;
    
    return {
      imageLeftAbsolute,
      imageRightAbsolute,
      imageTopAbsolute,
      imageBottomAbsolute
    };
  };

  // Helper function to calculate crop overlay dimensions and position
  const calculateCropOverlay = (
    upscaledDimensions: { width: number; height: number },
    displayedWidth: number,
    displayedHeight: number,
    offset: number,
    imageLeftAbsolute: number,
    imageTopAbsolute: number,
    imageBottomAbsolute: number,
    containerWidth: number,
    containerHeight: number,
    isPortrait: boolean
  ) => {
    if (isPortrait) {
      // Portrait: vertical cropping
      const maxCrop = upscaledDimensions.height - TARGET_SIZE;
      
      if (maxCrop <= 0) {
        return null; // No cropping needed
      }
      
      // Crop overlay should be full image width (since upscaled image is 1080px wide)
      const finalCropWidth = displayedWidth;
      
      // For a square crop (1080x1080), height should equal width in displayed pixels
      const finalCropHeight = Math.min(displayedWidth, displayedHeight);
      
      // Calculate crop overlay position relative to displayed image
      // Vertical position: map verticalOffset (0-1) to the available vertical range in displayed pixels
      const availableVerticalRange = Math.max(0, displayedHeight - finalCropHeight);
      const cropOverlayTopAbsolute = imageTopAbsolute + (offset * availableVerticalRange);
      const cropOverlayBottomAbsolute = cropOverlayTopAbsolute + finalCropHeight;
      
      // Horizontal position: crop overlay spans full width (centered)
      const cropOverlayLeftAbsolute = imageLeftAbsolute;
      const cropOverlayRightAbsolute = imageLeftAbsolute + finalCropWidth;
      
      return {
        finalCropWidth,
        finalCropHeight,
        cropOverlayLeftAbsolute,
        cropOverlayRightAbsolute,
        cropOverlayTopAbsolute,
        cropOverlayBottomAbsolute
      };
    } else {
      // Landscape: horizontal cropping
      const maxCrop = upscaledDimensions.width - TARGET_SIZE;
      
      if (maxCrop <= 0) {
        return null; // No cropping needed
      }
      
      // Crop overlay should be full image height (since upscaled image is 1080px tall)
      const finalCropHeight = displayedHeight;
      
      // For a square crop (1080x1080), width should equal height in displayed pixels
      const finalCropWidth = Math.min(displayedHeight, displayedWidth);
      
      // Calculate crop overlay position relative to displayed image
      // Horizontal position: map horizontalOffset (0-1) to the available horizontal range in displayed pixels
      const availableHorizontalRange = Math.max(0, displayedWidth - finalCropWidth);
      const cropOverlayLeftAbsolute = imageLeftAbsolute + (offset * availableHorizontalRange);
      const cropOverlayRightAbsolute = cropOverlayLeftAbsolute + finalCropWidth;
      
      // Vertical position: crop overlay spans full height (no centering needed)
      const cropOverlayTopAbsolute = imageTopAbsolute;
      const cropOverlayBottomAbsolute = imageTopAbsolute + finalCropHeight;
      
      return {
        finalCropWidth,
        finalCropHeight,
        cropOverlayLeftAbsolute,
        cropOverlayRightAbsolute,
        cropOverlayTopAbsolute,
        cropOverlayBottomAbsolute
      };
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto w-[95vw] sm:w-full">
        <DialogHeader>
          <DialogTitle>Crop Image</DialogTitle>
          <DialogDescription>
            {isPortrait 
              ? "Image will be upscaled to 1080px width. Click and drag the crop area to adjust vertical position."
              : "Image will be upscaled to 1080px height. Click and drag the crop area to adjust horizontal position."}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Main upscaled image with crop overlay and controls */}
          <div 
            ref={containerRef}
            className="relative w-full bg-transparent rounded-lg overflow-hidden select-none flex items-center justify-center"
            style={{ height: 'clamp(300px, 50vh, 600px)' }}
          >
            {!upscaledImageUrl && (
              <div className="text-muted-foreground">Loading image...</div>
            )}
            {upscaledImageUrl && upscaledDimensions && (() => {
              // Use containerWidth and containerHeight if available, otherwise use a fallback calculation
              const effectiveContainerWidth = containerWidth > 0 ? containerWidth : (containerRef.current?.offsetWidth || 800);
              const effectiveContainerHeight = containerHeight > 0 ? containerHeight : (containerRef.current?.offsetHeight || 600);
              const imageAspectRatio = upscaledDimensions.width / upscaledDimensions.height;
              
              // Calculate actual displayed image dimensions (matching objectFit: contain behavior)
              const { displayedWidth, displayedHeight } = calculateDisplayedImageDimensions(
                effectiveContainerWidth,
                effectiveContainerHeight,
                imageAspectRatio
              );
              
              // Calculate image absolute position (matching CSS centering)
              const { imageLeftAbsolute, imageRightAbsolute, imageTopAbsolute, imageBottomAbsolute } = 
                calculateImagePosition(effectiveContainerWidth, effectiveContainerHeight, displayedWidth, displayedHeight);
              
              // Calculate crop overlay dimensions and position
              const currentOffset = isPortrait ? verticalOffset : horizontalOffset;
              const cropOverlayInfo = calculateCropOverlay(
                upscaledDimensions,
                displayedWidth,
                displayedHeight,
                currentOffset,
                imageLeftAbsolute,
                imageTopAbsolute,
                imageBottomAbsolute,
                effectiveContainerWidth,
                effectiveContainerHeight,
                isPortrait
              );
              
              // Check if cropping is needed
              const maxCrop = isPortrait 
                ? upscaledDimensions.height - TARGET_SIZE
                : upscaledDimensions.width - TARGET_SIZE;
              
              if (maxCrop <= 0 || !cropOverlayInfo) {
                // Image is square or smaller/shorter, no cropping needed
                return (
                  <>
                    <img
                      src={upscaledImageUrl}
                      alt="Upscaled"
                      className="absolute pointer-events-none"
                      style={{
                        height: `${displayedHeight}px`,
                        width: `${displayedWidth}px`,
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        objectFit: 'contain',
                      }}
                      draggable={false}
                    />
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className="bg-background/90 px-6 py-4 rounded-md text-center max-w-md">
                        <p className="text-sm font-semibold text-foreground mb-2">
                          Image is already square
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Click "Apply" to upscale to 1080x1080, add black bars on the sides, and resize to 1920x1080 for YouTube.
                        </p>
                      </div>
                    </div>
                  </>
                );
              }

              const {
                finalCropWidth,
                finalCropHeight,
                cropOverlayLeftAbsolute,
                cropOverlayRightAbsolute,
                cropOverlayTopAbsolute,
                cropOverlayBottomAbsolute
              } = cropOverlayInfo;
              
              return (
                <>
                    <img
                      src={upscaledImageUrl}
                      alt="Upscaled"
                      className="absolute pointer-events-none"
                      style={{
                        height: `${displayedHeight}px`,
                        width: `${displayedWidth}px`,
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        objectFit: 'contain',
                      }}
                      draggable={false}
                    />
                  
                  {isPortrait ? (
                    <>
                      {/* Top dark side - from image top edge to crop overlay top edge */}
                      {cropOverlayTopAbsolute > imageTopAbsolute && (
                        <div
                          className="absolute pointer-events-none"
                          style={{
                            left: `${imageLeftAbsolute}px`,
                            top: `${imageTopAbsolute}px`,
                            width: `${displayedWidth}px`,
                            height: `${cropOverlayTopAbsolute - imageTopAbsolute}px`,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          }}
                        />
                      )}
                      
                      {/* Bottom dark side - from crop overlay bottom edge to image bottom edge */}
                      {cropOverlayBottomAbsolute < imageBottomAbsolute && (
                        <div
                          className="absolute pointer-events-none"
                          style={{
                            left: `${imageLeftAbsolute}px`,
                            top: `${cropOverlayBottomAbsolute}px`,
                            width: `${displayedWidth}px`,
                            height: `${imageBottomAbsolute - cropOverlayBottomAbsolute}px`,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {/* Left dark side - from image left edge to crop overlay left edge */}
                      {cropOverlayLeftAbsolute > imageLeftAbsolute && (
                        <div
                          className="absolute pointer-events-none"
                          style={{
                            left: `${imageLeftAbsolute}px`,
                            top: `${imageTopAbsolute}px`,
                            width: `${cropOverlayLeftAbsolute - imageLeftAbsolute}px`,
                            height: `${displayedHeight}px`,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          }}
                        />
                      )}
                      
                      {/* Right dark side - from crop overlay right edge to image right edge */}
                      {cropOverlayRightAbsolute < imageRightAbsolute && (
                        <div
                          className="absolute pointer-events-none"
                          style={{
                            left: `${cropOverlayRightAbsolute}px`,
                            top: `${imageTopAbsolute}px`,
                            width: `${imageRightAbsolute - cropOverlayRightAbsolute}px`,
                            height: `${displayedHeight}px`,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          }}
                        />
                      )}
                    </>
                  )}
                  
                  {/* Crop overlay - 1080x1080 square */}
                  <div
                    className="absolute cursor-grab active:cursor-grabbing md:cursor-grab md:active:cursor-grabbing pointer-events-none md:pointer-events-auto touch-none md:touch-auto"
                    style={{
                      width: `${finalCropWidth}px`,
                      height: `${finalCropHeight}px`,
                      left: `${cropOverlayLeftAbsolute}px`,
                      top: `${cropOverlayTopAbsolute}px`,
                      outline: '3px solid white',
                      outlineOffset: '-3px',
                      boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.5)',
                    }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                  />
                </>
              );
            })()}
          </div>

          {/* Position indicator */}
          <div className="flex flex-col items-center space-y-3">
            <div className="flex justify-center items-center space-x-2 text-sm text-muted-foreground">
              {isPortrait ? (
                <>
                  <span>Position: {Math.round(verticalOffset * 100)}%</span>
                  <span className="text-xs">
                    {(() => {
                      const percentage = Math.round(verticalOffset * 100);
                      if (percentage < 50) return '(Top)';
                      if (percentage > 50) return '(Bottom)';
                      return '(Center)';
                    })()}
                  </span>
                </>
              ) : (
                <>
                  <span>Position: {Math.round(horizontalOffset * 100)}%</span>
                  <span className="text-xs">
                    {(() => {
                      const percentage = Math.round(horizontalOffset * 100);
                      if (percentage < 50) return '(Left)';
                      if (percentage > 50) return '(Right)';
                      return '(Center)';
                    })()}
                  </span>
                </>
              )}
            </div>
            
            {/* Mobile-only slider - show on mobile */}
            <div className="w-full px-4 lg:hidden">
              <Slider
                value={[isPortrait ? verticalOffset : horizontalOffset]}
                onValueChange={(value) => {
                  if (isPortrait) {
                    setVerticalOffset(value[0]);
                  } else {
                    setHorizontalOffset(value[0]);
                  }
                }}
                min={0}
                max={1}
                step={0.01}
                className="w-full"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleApply}>
            Apply
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
